import React, { useState, useRef, useMemo } from "react";
import { ContentHeader } from "@components";
import DataTable from "../components/datatable/DataTable";
import UserTypeModal from "../components/modal/UserTypeModal";
import API from "../api";
import toastr from "toastr";

interface UserType {
  id?: number;
  user_type_name: string;
  status: string;
  dashboard?: number;
  users?: number;
  adduser?: number;
  edituser?: number;
  deleteuser?: number;
  manage_user_permission?: number;
  patients?: number;
  addpatient?: number;
  editpatient?: number;
  deletepatient?: number;
  investigation?: number;
  doctor?: number;
  add_doctor?: number;
  edit_doctor?: number;
  delete_doctor?: number;
  billing?: number;
  report?: number;
  view_report?: number;
  delete_report?: number;
  payments?: number;
  finance?: number;
  income?: number;
  expense?: number;
  finance_report?: number;
  test_settings?: number;
}

const UserTypePage: React.FC = () => {
  const [showModal, setShowModal]               = useState(false);
  const [selectedUserType, setSelectedUserType] = useState<UserType | null>(null);
  const [modalMode, setModalMode]               = useState<"add" | "edit">("add");
  const tableRef = useRef<any>(null);

  // ── Fetch user types ─────────────────────────────────────────────────────────
  const fetchUserTypes = async (
    page: number,
    search: string,
    sortBy    = "user_type_name",
    sortOrder = "asc"
  ) => {
    try {
      const res = await API.get(
        `/usertype/getUsersType?page=${page}&pageSize=25&search=${search}&sortBy=${sortBy}&sortOrder=${sortOrder}`
      );
      return { data: res.data.data, total: res.data.total };
    } catch {
      toastr.error("Failed to fetch user types");
      return { data: [], total: 0 };
    }
  };

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const handleAdd = () => {
    setModalMode("add");
    setSelectedUserType(null);
    setShowModal(true);
  };

  const handleEdit = (userType: UserType) => {
    setModalMode("edit");
    setSelectedUserType(userType);
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this user type?")) return;
    try {
      await API.delete(`/usertype/deleteUserType/${id}`);
      toastr.success("User Type deleted successfully");
      tableRef.current?.refetch();
    } catch {
      toastr.error("Failed to delete user type");
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedUserType(null);
  };

  // ── Table columns ─────────────────────────────────────────────────────────────
  const columns = useMemo(
    () => [
      { header: "ID",             accessorKey: "id" },
      { header: "User Type Name", accessorKey: "user_type_name" },
      {
        header: "Status",
        accessorKey: "status",
        cell: ({ row }: any) => (
          <span
            className={`badge bg-${
              row.original.status == "1" ? "success" : "danger"
            }`}
          >
            {row.original.status == "1" ? "Active" : "Inactive"}
          </span>
        ),
      },
      {
        header: "Actions",
        id: "actions",
        cell: ({ row }: any) => {
          const data: UserType = row.original;
          return (
            <div className="d-flex justify-content-center gap-2">
              <button
                className="btn btn-light"
                onClick={() => handleEdit(data)}
                title="Edit"
              >
                <i className="fa fa-edit" />
              </button>
              <button
                className="btn btn-light"
                onClick={() => handleDelete(data.id!)}
                title="Delete"
              >
                <i className="fa fa-trash" />
              </button>
            </div>
          );
        },
      },
    ],
    []
  );

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div>
      <div className="content-header d-flex justify-content-between align-items-center">
        <ContentHeader title="User Type" />
        <button className="btn btn-light" onClick={handleAdd}>
          <i className="fa fa-plus me-1" /> Add User Type
        </button>
      </div>

      <section className="content">
        <div className="container-fluid">
          <div className="card card-outline card-primary shadow-sm">
            <div className="card-body">
              <DataTable
                ref={tableRef}
                columns={columns}
                queryFn={fetchUserTypes}
                queryKey={["usertype"]}
              />
            </div>
          </div>
        </div>
      </section>

      {showModal && (
        <UserTypeModal
          show={showModal}
          onClose={handleModalClose}
          mode={modalMode}
          userTypeData={selectedUserType}
          onSuccess={() => {
            handleModalClose();
            tableRef.current?.refetch();
          }}
        />
      )}
    </div>
  );
};

export default UserTypePage;